import type { ComponentType, FC, PropsWithChildren, ReactNode } from 'react';
import type React from 'react';
import { Component } from 'react';

interface ErrorBoundaryProps extends PropsWithChildren {
  notifier: {
    notify: (error: unknown, info: unknown) => void;
  };
}

class ErrorBoundary extends Component<ErrorBoundaryProps> {
  private notifier: ErrorBoundaryProps['notifier'];

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.notifier = props.notifier;
  }

  componentDidCatch(error, info) {
    this.notifier.notify(error, info);
  }

  render(): ReactNode {
    return this.props.children;
  }
}

export function withErrorBoundary<P>(WrappedComponent: ComponentType<P>, notifier: ErrorBoundaryProps['notifier']) {
  const ComponentWithErrorBoundary: FC<P & React.JSX.IntrinsicAttributes> = (props) => {
    return (
      <ErrorBoundary notifier={notifier}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };

  ComponentWithErrorBoundary.displayName = `WithErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return ComponentWithErrorBoundary;
}

export default ErrorBoundary;
